import React from 'react';
import { motion } from 'framer-motion';
import { Phase } from '../types';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface Props {
  phases: Phase[];
}

const statusColors: Record<string, string> = {
  pending: '#334155',
  assigned: '#f59e0b',
  in_progress: '#3b82f6',
  completed: '#22c55e',
  failed: '#ef4444',
};

const statusIcons: Record<string, string> = {
  pending: '',
  assigned: '~',
  in_progress: '...',
  completed: '✓',
  failed: '✗',
};

// Active phase = something is or might be running on it. Soft DELETE
// returns 409 in that state, so the UI routes through /abandon
// instead — one button that cancels active tasks, resets stuck
// modules, AND deletes the phase row in a single backend call.
function isPhaseActive(phase: Phase): boolean {
  return phase.status === 'in_progress' || phase.status === 'review';
}

async function deletePhase(phase: Phase): Promise<void> {
  // For terminal phases — plain DELETE, gentle confirm.
  if (!isPhaseActive(phase)) {
    if (!window.confirm(`Удалить фазу "${phase.name}" из Pipeline? Все модули и история по ней пропадут.`)) return;
    try {
      const r = await fetch(`${API}/api/phases/${phase.id}`, { method: 'DELETE' });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        alert(`Не удалось удалить: ${data.message || data.error || `HTTP ${r.status}`}`);
      }
    } catch (err: any) {
      alert(`Сетевая ошибка: ${err.message || 'unknown'}`);
    }
    return;
  }

  // For active phases — abandon flow. Stronger warning because we're
  // SIGTERM-ing live Claude work and locking in whatever it spent.
  const stuckCount = phase.modules.filter((m) =>
    ['assigned', 'in_progress', 'self_check', 'needs_fix'].includes(m.status),
  ).length;
  const warn =
    `Бросить фазу "${phase.name}"?\n\n` +
    `• Активные задачи будут остановлены (SIGTERM Claude).\n` +
    `• Текущая трата зафиксируется в Истории.\n` +
    `• ${stuckCount} модуль${stuckCount === 1 ? '' : 'я/ей'} в работе будут сброшены.\n` +
    `• Фаза удалится из Pipeline.\n\n` +
    `Действие необратимо.`;
  if (!window.confirm(warn)) return;
  try {
    const r = await fetch(`${API}/api/phases/${phase.id}/abandon`, { method: 'POST' });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      alert(`Не удалось бросить фазу: ${data.message || data.error || `HTTP ${r.status}`}`);
    }
  } catch (err: any) {
    alert(`Сетевая ошибка: ${err.message || 'unknown'}`);
  }
}

export const PipelineView: React.FC<Props> = ({ phases }) => {
  if (phases.length === 0) {
    return (
      <div className="pipeline-view">
        <h3 className="pipeline-title">Pipeline</h3>
        <div className="pipeline-empty">
          <p>Нет загруженных фаз</p>
          <p className="pipeline-hint">
            Перейдите во вкладку "Задачи" и загрузите .md файл фазы
          </p>
        </div>
      </div>
    );
  }

  const totalModules = phases.reduce((s, p) => s + p.modules.length, 0);
  const completedModules = phases.reduce(
    (s, p) => s + p.modules.filter((m) => m.status === 'completed').length, 0
  );

  return (
    <div className="pipeline-view">
      <div className="pipeline-header">
        <h3 className="pipeline-title">Pipeline</h3>
        <span className="pipeline-progress-text">
          {completedModules}/{totalModules} модулей
        </span>
      </div>

      {phases.map((phase) => {
        const done = phase.modules.filter((m) => m.status === 'completed').length;
        const pct = phase.modules.length > 0 ? Math.round((done / phase.modules.length) * 100) : 0;

        return (
          <div key={phase.id} className="pipeline-phase">
            <div className="phase-name-row">
              <span className="phase-name">{phase.name}</span>
              <span className="phase-name-actions">
                <button
                  className={`pipeline-phase-delete${isPhaseActive(phase) ? ' pipeline-phase-abandon' : ''}`}
                  onClick={() => deletePhase(phase)}
                  title={isPhaseActive(phase)
                    ? 'Бросить фазу: остановить активные задачи, сбросить модули, удалить фазу'
                    : 'Удалить фазу'}
                  aria-label={isPhaseActive(phase) ? 'Бросить фазу' : 'Удалить фазу'}
                >
                  {isPhaseActive(phase) ? '◼ Бросить' : '× Удалить'}
                </button>
                <span className="phase-pct">{pct}%</span>
              </span>
            </div>
            <div className="pipeline-bar">
              <motion.div
                className="pipeline-bar-fill"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <div className="phase-modules">
              {phase.modules.map((mod, i) => (
                <motion.div
                  key={mod.id}
                  className="pipeline-module"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.03 }}
                  style={{
                    background: statusColors[mod.status],
                    color: mod.status === 'pending' ? '#94a3b8' : '#fff',
                  }}
                  title={mod.assignedTo ? `→ ${mod.assignedTo}` : ''}
                >
                  {statusIcons[mod.status]}{statusIcons[mod.status] ? ' ' : ''}{mod.name}
                </motion.div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
